import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, ArrowLeft, LogOut, Loader2, Star, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { AGES, POURQUOI } from "@/lib/kidsData";
import { MemoryGame, ImageWordGame, WhatIsItGame, IntrusGame, RelieGame, EcouteGame, CompteGame, CouleurGame, SuiteGame, OmbreGame } from "@/components/kids/KidsGames";
import { DessineMoi } from "@/components/kids/DessineMoi";

const T = {
  fr: { title: "Le coin des curieux", subtitle: "Des jeux et des pourquoi, rien que pour toi.", chooseAge: "Quel âge as-tu ?",
        curiosity: "Le pourquoi du jour", listen: "Écouter", quit: "Sortir", changeAge: "Changer d'âge",
        bravo: "Bravo ! Tu as gagné une étoile !", replay: "Rejouer", back: "Retour aux jeux",
        gateTitle: "Un adulte, s'il te plaît", gateQ: (a, b) => `Combien font ${a} + ${b} ?`, gatePh: "Réponse", gateGo: "Valider", gateBad: "Essaie encore",
        memoryHint: "Retrouve les paires identiques.", wordHint: "Quel est ce mot ?", whatHint: "Devine, puis touche le bon mot !",
        intrusHint: "Touche celui qui n'est pas comme les autres.", relieHint: "Relie chaque animal à sa maison.", round: "Question",
        ecouteHint: "Écoute bien, puis touche la bonne image !", ecouteReplay: "Réécouter",
        compteHint: "Combien y en a-t-il ? Touche le bon nombre.",
        couleurHint: "Touche la bonne couleur !",
        suiteHint: "Qu'est-ce qui vient après ?",
        ombreHint: "À quel objet appartient cette ombre ?",
        sayFound: (w) => `Oui, c'est ${w} !`, sayFind: (w) => `Écoute : ${w}.`, sayCount: (n) => `Il y en a ${n} !`,
        dessineHint: "Dis ou choisis ce que tu veux que je dessine !", dessineMic: "Appuie et parle", dessineListening: "Je t'écoute...", dessineHearing: "J'écoute tes mots...",
        dessineThinking: "Je dessine ton idée...", dessineAgain: "Encore un dessin", dessineDraw: "Dessine !", dessinePlaceholder: "un chat qui vole...",
        dessineIdeasLabel: "Ou choisis une idée rigolote :", dessineIdeas: ["un chat astronaute", "un château de bonbons", "un dinosaure arc-en-ciel", "une licorne", "un robot rigolo", "une fusée dans les étoiles"],
        dessineOops: "Essaie plutôt une idée toute douce !", dessineFail: "Oups, mon crayon a glissé, réessaie !", dessineMicDenied: "Je n'entends pas ton micro, choisis une idée !",
        games: { memory: "Les paires", imageword: "L'image et le mot", intrus: "Trouve l'intrus", whatisit: "Qu'est-ce que c'est ?", relie: "Chaque animal chez lui", dessine: "Dessine-moi", ecoute: "Les sons de la nature", compte: "Compte avec moi", couleur: "La bonne couleur", suite: "La suite logique", ombre: "L'ombre mystère" } },
  en: { title: "The curious corner", subtitle: "Games and whys, just for you.", chooseAge: "How old are you?",
        curiosity: "Why of the day", listen: "Listen", quit: "Exit", changeAge: "Change age",
        bravo: "Bravo! You earned a star!", replay: "Play again", back: "Back to games",
        gateTitle: "A grown-up, please", gateQ: (a, b) => `What is ${a} + ${b}?`, gatePh: "Answer", gateGo: "OK", gateBad: "Try again",
        memoryHint: "Find the matching pairs.", wordHint: "What is this word?", whatHint: "Guess, then tap the right word!",
        intrusHint: "Tap the one that doesn't belong.", relieHint: "Match each animal to its home.", round: "Question",
        ecouteHint: "Listen well, then tap the right picture!", ecouteReplay: "Play again",
        compteHint: "How many are there? Tap the right number.",
        couleurHint: "Tap the right colour!",
        suiteHint: "What comes next?",
        ombreHint: "Which object made this shadow?",
        sayFound: (w) => `Yes, it's ${w}!`, sayFind: (w) => `Listen: ${w}.`, sayCount: (n) => `There are ${n}!`,
        dessineHint: "Say or pick what you want me to draw!", dessineMic: "Tap and talk", dessineListening: "I'm listening...", dessineHearing: "Hearing your words...",
        dessineThinking: "Drawing your idea...", dessineAgain: "Another drawing", dessineDraw: "Draw!", dessinePlaceholder: "a flying cat...",
        dessineIdeasLabel: "Or pick a fun idea:", dessineIdeas: ["an astronaut cat", "a candy castle", "a rainbow dinosaur", "a unicorn", "a silly robot", "a rocket in the stars"],
        dessineOops: "Try a gentle, sweet idea instead!", dessineFail: "Oops, my pencil slipped, try again!", dessineMicDenied: "I can't hear your mic, pick an idea!",
        games: { memory: "Pairs", imageword: "Picture & word", intrus: "Find the odd one", whatisit: "What is it?", relie: "Every animal home", dessine: "Draw for me", ecoute: "Nature sounds", compte: "Count with me", couleur: "The right colour", suite: "What comes next", ombre: "The mystery shadow" } },
  es: { title: "El rincón curioso", subtitle: "Juegos y porqués, solo para ti.", chooseAge: "¿Cuántos años tienes?",
        curiosity: "El porqué del día", listen: "Escuchar", quit: "Salir", changeAge: "Cambiar edad",
        bravo: "¡Bravo! ¡Ganaste una estrella!", replay: "Jugar otra vez", back: "Volver a los juegos",
        gateTitle: "Un adulto, por favor", gateQ: (a, b) => `¿Cuánto es ${a} + ${b}?`, gatePh: "Respuesta", gateGo: "OK", gateBad: "Inténtalo otra vez",
        memoryHint: "Encuentra las parejas iguales.", wordHint: "¿Qué palabra es?", whatHint: "¡Adivina y toca la palabra correcta!",
        intrusHint: "Toca el que no es como los demás.", relieHint: "Une cada animal con su casa.", round: "Pregunta",
        ecouteHint: "¡Escucha bien y toca la imagen correcta!", ecouteReplay: "Escuchar otra vez",
        compteHint: "¿Cuántos hay? Toca el número correcto.",
        couleurHint: "¡Toca el color correcto!",
        suiteHint: "¿Qué viene después?",
        ombreHint: "¿De qué objeto es esta sombra?",
        sayFound: (w) => `¡Sí, es ${w}!`, sayFind: (w) => `Escucha: ${w}.`, sayCount: (n) => `¡Hay ${n}!`,
        dessineHint: "¡Di o elige lo que quieres que dibuje!", dessineMic: "Toca y habla", dessineListening: "Te escucho...", dessineHearing: "Escuchando tus palabras...",
        dessineThinking: "Dibujando tu idea...", dessineAgain: "Otro dibujo", dessineDraw: "¡Dibuja!", dessinePlaceholder: "un gato que vuela...",
        dessineIdeasLabel: "O elige una idea divertida:", dessineIdeas: ["un gato astronauta", "un castillo de dulces", "un dinosaurio arcoíris", "un unicornio", "un robot gracioso", "un cohete entre estrellas"],
        dessineOops: "¡Prueba mejor una idea dulce y suave!", dessineFail: "¡Ups, se me resbaló el lápiz, inténtalo otra vez!", dessineMicDenied: "No oigo tu micro, ¡elige una idea!",
        games: { memory: "Las parejas", imageword: "Imagen y palabra", intrus: "Encuentra el intruso", whatisit: "¿Qué es?", relie: "Cada animal en casa", dessine: "Dibújame", ecoute: "Los sonidos de la naturaleza", compte: "Cuenta conmigo", couleur: "El color correcto", suite: "¿Qué sigue?", ombre: "La sombra misteriosa" } },
  nl: { title: "De nieuwsgierige hoek", subtitle: "Spelletjes en waaroms, speciaal voor jou.", chooseAge: "Hoe oud ben je?",
        curiosity: "Waarom van de dag", listen: "Luister", quit: "Afsluiten", changeAge: "Leeftijd wijzigen",
        bravo: "Bravo! Je verdiende een ster!", replay: "Opnieuw", back: "Terug naar de spellen",
        gateTitle: "Een volwassene, alsjeblieft", gateQ: (a, b) => `Hoeveel is ${a} + ${b}?`, gatePh: "Antwoord", gateGo: "OK", gateBad: "Probeer opnieuw",
        memoryHint: "Vind de gelijke paren.", wordHint: "Welk woord is dit?", whatHint: "Raad en tik het juiste woord!",
        intrusHint: "Tik degene die er niet bij hoort.", relieHint: "Verbind elk dier met zijn huis.", round: "Vraag",
        ecouteHint: "Luister goed en tik het juiste plaatje!", ecouteReplay: "Opnieuw luisteren",
        compteHint: "Hoeveel zijn er? Tik het juiste getal.",
        couleurHint: "Tik de juiste kleur!",
        suiteHint: "Wat komt hierna?",
        ombreHint: "Welk voorwerp maakte deze schaduw?",
        sayFound: (w) => `Ja, het is ${w}!`, sayFind: (w) => `Luister: ${w}.`, sayCount: (n) => `Er zijn er ${n}!`,
        dessineHint: "Zeg of kies wat ik moet tekenen!", dessineMic: "Tik en praat", dessineListening: "Ik luister...", dessineHearing: "Ik hoor je woorden...",
        dessineThinking: "Ik teken je idee...", dessineAgain: "Nog een tekening", dessineDraw: "Teken!", dessinePlaceholder: "een vliegende kat...",
        dessineIdeasLabel: "Of kies een grappig idee:", dessineIdeas: ["een astronautenkat", "een snoepkasteel", "een regenboogdinosaurus", "een eenhoorn", "een gekke robot", "een raket tussen de sterren"],
        dessineOops: "Probeer liever een zacht, lief idee!", dessineFail: "Oeps, mijn potlood gleed uit, probeer opnieuw!", dessineMicDenied: "Ik hoor je microfoon niet, kies een idee!",
        games: { memory: "De paren", imageword: "Beeld en woord", intrus: "Vind de indringer", whatisit: "Wat is het?", relie: "Elk dier thuis", dessine: "Teken voor mij", ecoute: "Geluiden van de natuur", compte: "Tel met mij", couleur: "De juiste kleur", suite: "Wat komt hierna?", ombre: "De mysterieuze schaduw" } },
};

const GAME_META = [
  { id: "memory", emoji: "🧠", comp: MemoryGame, color: "from-amber-500/25 to-orange-500/10" },
  { id: "imageword", emoji: "🔤", comp: ImageWordGame, color: "from-sky-500/25 to-cyan-500/10" },
  { id: "intrus", emoji: "🕵️", comp: IntrusGame, color: "from-violet-500/25 to-fuchsia-500/10" },
  { id: "whatisit", emoji: "🔍", comp: WhatIsItGame, color: "from-emerald-500/25 to-teal-500/10" },
  { id: "relie", emoji: "🏡", comp: RelieGame, color: "from-rose-500/25 to-pink-500/10" },
  { id: "ecoute", emoji: "🎧", comp: EcouteGame, color: "from-teal-500/25 to-emerald-500/10" },
  { id: "compte", emoji: "🔢", comp: CompteGame, color: "from-indigo-500/25 to-blue-500/10" },
  { id: "couleur", emoji: "🌈", comp: CouleurGame, color: "from-fuchsia-500/25 to-pink-500/10" },
  { id: "suite", emoji: "🧩", comp: SuiteGame, color: "from-amber-500/25 to-yellow-500/10" },
  { id: "ombre", emoji: "🌑", comp: OmbreGame, color: "from-slate-500/25 to-gray-500/10" },
];

export default function EspaceEnfant() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (i18n.language || "fr").slice(0, 2);
  const L = T[lang] || T.fr;

  const [age, setAge] = useState(() => localStorage.getItem("coccon_kids_age") || null);
  const [view, setView] = useState("hub"); // hub | game id
  const [gameKey, setGameKey] = useState(0);
  const [stars, setStars] = useState(() => parseInt(localStorage.getItem("coccon_kids_stars") || "0", 10));
  const [won, setWon] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [gate, setGate] = useState(null); // pending action: 'quit' | 'age'
  const [gateSum, setGateSum] = useState({ a: 0, b: 0 });
  const [gateVal, setGateVal] = useState("");
  const [gateErr, setGateErr] = useState(false);

  const ageCfg = AGES.find((a) => a.id === age) || AGES[1];
  const pourquoi = useMemo(() => {
    const list = POURQUOI[lang] || POURQUOI.fr;
    const day = Math.floor(Date.now() / 86400000);
    return list[day % list.length];
  }, [lang]);

  const speak = async (text) => {
    if (speaking) return;
    setSpeaking(true);
    try {
      const { data } = await api.post("/kids/speak", { text: text.slice(0, 320) });
      const audio = new Audio("data:audio/mpeg;base64," + data.audio);
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch {
      setSpeaking(false);
    }
  };

  const chooseAge = (id) => { setAge(id); localStorage.setItem("coccon_kids_age", id); };

  const onWin = () => {
    const ns = stars + 1;
    setStars(ns); localStorage.setItem("coccon_kids_stars", String(ns));
    setWon(true);
    speak(L.bravo);
  };
  const rewardStar = () => {
    setStars((s) => { const ns = s + 1; localStorage.setItem("coccon_kids_stars", String(ns)); return ns; });
  };
  const replay = () => { setWon(false); setGameKey((k) => k + 1); };
  const toHub = () => { setWon(false); setView("hub"); };

  const openGate = (action) => {
    const a = 3 + Math.floor(Math.random() * 6);
    const b = 3 + Math.floor(Math.random() * 6);
    setGateSum({ a, b }); setGateVal(""); setGateErr(false); setGate(action);
  };
  const submitGate = () => {
    if (parseInt(gateVal, 10) === gateSum.a + gateSum.b) {
      const action = gate; setGate(null);
      if (action === "quit") navigate("/");
      else if (action === "age") { setView("hub"); setAge(null); localStorage.removeItem("coccon_kids_age"); }
    } else { setGateErr(true); }
  };

  /* ---------- Sélection d'âge ---------- */
  if (!age) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">
        <div className="kids-sky" aria-hidden />
        <h1 className="font-display text-gradient-orange text-4xl sm:text-5xl italic mb-3 text-center">{L.title}</h1>
        <p className="text-foreground/60 mb-10 text-center">{L.chooseAge}</p>
        <div className="grid sm:grid-cols-3 gap-5 w-full max-w-2xl">
          {AGES.map((a) => (
            <motion.button
              key={a.id} whileHover={{ scale: 1.04, y: -4 }} whileTap={{ scale: 0.97 }}
              onClick={() => chooseAge(a.id)} data-testid={`kids-age-${a.id}`}
              className="glass rounded-3xl p-8 border border-primary/20 flex flex-col items-center gap-3"
            >
              <span className="text-6xl">{a.emoji}</span>
              <span className="font-display text-2xl text-foreground/90">{a.label[lang] || a.label.fr}</span>
            </motion.button>
          ))}
        </div>
        <button onClick={() => navigate("/")} data-testid="kids-exit-simple" className="mt-10 text-foreground/40 hover:text-primary text-sm transition-colors">← COCOON</button>
      </div>
    );
  }

  const activeGame = GAME_META.find((g) => g.id === view);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="espace-enfant">
      <div className="kids-sky" aria-hidden />
      <div className="grain-overlay" aria-hidden style={{ opacity: 0.025 }} />

      {/* En-tête */}
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-4">
        <div className="flex items-center gap-2 font-display text-xl text-gradient-orange italic">
          {view === "hub" ? "🌈 " + L.title : (
            <button onClick={toHub} data-testid="kids-back-btn" className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors">
              <ArrowLeft size={20} /> {L.back}
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-primary font-semibold" data-testid="kids-stars">
            <Star size={18} className="fill-primary" /> {stars}
          </span>
          <button onClick={() => openGate("quit")} data-testid="kids-quit-btn" className="w-10 h-10 rounded-full flex items-center justify-center text-foreground/50 hover:text-primary hover:bg-primary/10 transition-colors" title={L.quit}>
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pb-20">
        {view === "hub" ? (
          <>
            <p className="text-center text-foreground/50 mb-8">{L.subtitle}</p>

            {/* Pourquoi du jour */}
            <div className="glass rounded-3xl p-6 sm:p-8 border border-primary/15 mb-10" data-testid="kids-curiosity">
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="uppercase tracking-[0.2em] text-xs text-primary/90">🤔 {L.curiosity}</span>
                <button onClick={() => speak(`${pourquoi.q} ${pourquoi.a}`)} data-testid="kids-listen-btn"
                  className="flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-primary/15 text-primary hover:bg-primary/25 transition-colors">
                  {speaking ? <Loader2 size={15} className="animate-spin" /> : <Volume2 size={15} />} {L.listen}
                </button>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl text-foreground/90 mb-2">{pourquoi.q}</h3>
              <p className="text-foreground/70 text-lg leading-relaxed">{pourquoi.a}</p>
            </div>

            {/* Jeux */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {GAME_META.map((g) => (
                <motion.button
                  key={g.id} whileHover={{ scale: 1.04, y: -4 }} whileTap={{ scale: 0.97 }}
                  onClick={() => { setGameKey((k) => k + 1); setView(g.id); }}
                  data-testid={`kids-game-${g.id}`}
                  className={`rounded-3xl p-6 border border-white/10 bg-gradient-to-br ${g.color} flex flex-col items-center gap-3 text-center`}
                >
                  <span className="text-5xl">{g.emoji}</span>
                  <span className="font-display text-lg text-foreground/90 leading-tight">{L.games[g.id]}</span>
                </motion.button>
              ))}
              <motion.button
                whileHover={{ scale: 1.04, y: -4 }} whileTap={{ scale: 0.97 }}
                onClick={() => { setGameKey((k) => k + 1); setView("dessine"); }}
                data-testid="kids-game-dessine"
                className="rounded-3xl p-6 border border-primary/25 bg-gradient-to-br from-primary/25 to-amber-500/10 flex flex-col items-center gap-3 text-center relative overflow-hidden"
              >
                <span className="text-5xl">🎨</span>
                <span className="font-display text-lg text-foreground/90 leading-tight">{L.games.dessine}</span>
                <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider text-primary/80 bg-primary/15 rounded-full px-2 py-0.5">IA</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.04, y: -4 }} whileTap={{ scale: 0.97 }}
                onClick={() => openGate("age")} data-testid="kids-change-age"
                className="rounded-3xl p-6 border border-white/10 bg-white/5 flex flex-col items-center gap-3 text-center"
              >
                <span className="text-5xl">{ageCfg.emoji}</span>
                <span className="font-display text-lg text-foreground/70 leading-tight">{L.changeAge}</span>
              </motion.button>
            </div>
          </>
        ) : view === "dessine" ? (
          <div className="pt-2">
            <h2 className="font-display text-3xl text-center text-gradient-orange italic mb-6">🎨 {L.games.dessine}</h2>
            <DessineMoi lang={lang} speak={speak} L={L} reward={rewardStar} />
          </div>
        ) : activeGame ? (
          <div className="pt-2">
            <h2 className="font-display text-3xl text-center text-gradient-orange italic mb-6">{L.games[activeGame.id]}</h2>
            <activeGame.comp key={gameKey} age={ageCfg} lang={lang} onWin={onWin} speak={speak} L={L} />
          </div>
        ) : null}
      </main>

      {/* Bravo */}
      <AnimatePresence>
        {won && (
          <motion.div
            className="fixed inset-0 z-[130] flex items-center justify-center p-6 bg-background/80 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="kids-bravo"
          >
            <motion.div
              initial={{ scale: 0.7, y: 20 }} animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 16 }}
              className="relative glass rounded-3xl p-10 text-center max-w-sm w-full border border-primary/30 overflow-hidden"
            >
              <div className="anniv-confetti" aria-hidden />
              <div className="relative">
                <div className="text-7xl mb-4">🎉</div>
                <h3 className="font-display text-3xl text-gradient-orange italic mb-2">{L.bravo}</h3>
                <div className="flex items-center justify-center gap-1 text-primary text-2xl mb-7">
                  <Star size={26} className="fill-primary pulse-star" /> +1
                </div>
                <div className="flex gap-3">
                  <button onClick={replay} data-testid="kids-replay-btn" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors">
                    <RefreshCw size={16} /> {L.replay}
                  </button>
                  <button onClick={toHub} data-testid="kids-tohub-btn" className="flex-1 py-3 rounded-full border border-white/15 text-foreground/80 hover:bg-white/5 transition-colors">
                    {L.back}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Portail parental */}
      <AnimatePresence>
        {gate && (
          <motion.div
            className="fixed inset-0 z-[130] flex items-center justify-center p-6 bg-background/85 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="kids-gate"
          >
            <div className="glass rounded-3xl p-8 text-center max-w-xs w-full border border-primary/25">
              <p className="uppercase tracking-[0.2em] text-xs text-primary/90 mb-4">🔒 {L.gateTitle}</p>
              <p className="font-display text-2xl text-foreground/90 mb-5">{L.gateQ(gateSum.a, gateSum.b)}</p>
              <input
                type="number" inputMode="numeric" value={gateVal}
                onChange={(e) => { setGateVal(e.target.value); setGateErr(false); }}
                onKeyDown={(e) => e.key === "Enter" && submitGate()}
                data-testid="kids-gate-input" placeholder={L.gatePh}
                className={`w-full text-center text-xl rounded-2xl bg-white/5 border-2 outline-none px-4 py-3 mb-3 ${gateErr ? "border-rose-400" : "border-white/15 focus:border-primary/50"}`}
              />
              {gateErr && <p className="text-rose-400 text-sm mb-3">{L.gateBad}</p>}
              <div className="flex gap-3">
                <button onClick={() => setGate(null)} className="flex-1 py-2.5 rounded-full border border-white/15 text-foreground/70 hover:bg-white/5 transition-colors">✕</button>
                <button onClick={submitGate} data-testid="kids-gate-submit" className="flex-1 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors">{L.gateGo}</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
